import { format } from "date-fns";
import { History, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PromptHistoryDialog } from "@/components/ui/prompt-history-dialog";
import { Textarea } from "@/components/ui/textarea";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { SectionCard } from "@/components/ui/section-card";
import {
  useCompanyContext,
  useCompanyContextHistory,
  useRollbackCompanyContext,
  useUpdateCompanyContext,
} from "@/features/company-context/hooks/useCompanyContext";
import { cn } from "@/lib/utils";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

const MAX = 500;

export function CompanyContextPage() {
  const { t } = useTranslation("companyContext");
  const { t: tc } = useTranslation("common");
  const { data, isLoading, isError, error, refetch } = useCompanyContext();
  const update = useUpdateCompanyContext();
  const canUpdate = useCan(Action.COMPANY_CONTEXT_UPDATED);
  const [content, setContent] = useState<string>("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const history = useCompanyContextHistory(historyOpen);
  const rollback = useRollbackCompanyContext();

  useEffect(() => {
    if (data) setContent(data.content);
  }, [data]);

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const length = content.length;
  const exceeded = length > MAX;
  const nearLimit = length > MAX - 20;
  const dirty = (data?.content ?? "") !== content;

  async function handleSave() {
    try {
      await update.mutateAsync(content);
      toast.success(t("toasts.saved"));
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <PageHeader title={t("title")} description={t("description")} />

      <SectionCard>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="content">{t("label")}</Label>
            {canUpdate ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setHistoryOpen(true)}
              >
                <History className="h-3.5 w-3.5" />
                {tc("actions.history")}
              </Button>
            ) : null}
          </div>
          <Textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={8}
            disabled={!canUpdate}
            placeholder={t("placeholder")}
          />
          <div
            className={cn(
              "flex justify-between text-xs",
              nearLimit ? "text-danger-700" : "text-ink-500",
            )}
          >
            <span>
              {data?.updated_at
                ? t("updatedAt", {
                    date: format(new Date(data.updated_at), "dd/MM/yyyy HH:mm"),
                  })
                : t("neverConfigured")}
            </span>
            <span>
              {length}/{MAX}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setContent(data?.content ?? "")}
            disabled={!dirty || update.isPending}
          >
            {tc("actions.discard")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canUpdate || !dirty || exceeded || update.isPending}
          >
            {update.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            {tc("actions.save")}
          </Button>
        </div>
      </SectionCard>

      <PromptHistoryDialog
        resourceLabel={t("resourceLabel")}
        versions={history.data ?? []}
        isLoading={history.isLoading}
        currentPrompt={data?.content ?? ""}
        onRollback={(version) => rollback.mutateAsync({ version })}
        isRollingBack={rollback.isPending}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </div>
  );
}
